import { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  ActionMenu,
  Modal,
  SimpleHelpText,
  Header,
  NavigateBeforeButton,
} from "@components";
import {
  useActionMenu,
  useDeleteTemplate,
  usePopulateRequestWithTemplate,
  useRequestData,
  useTemplates,
} from "@hooks";
import { Template, TemplateType } from "@interfaces";
import { buildTemplateDescription } from "../utils";

import {
  Container as StyledContainer,
  DeleteIcon,
  EditIcon,
  EmptyStateText,
  FullScreenContainer,
  FullScreenTemplatesList,
  HeaderRow,
  InfoIcon,
  LoadingContainer,
  LoadingOverlay,
  MoreVertIcon,
  NewTemplateButton,
  TemplateDescription,
  TemplateItem,
  TemplateItemContent,
  TemplateName,
  TemplatesList,
  Title,
  TitleContainer,
  ViewAllButton,
} from "./TemplatesSection.styles";

function TruncatedTemplateName({ name }: { name: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [name]);

  return (
    <Tooltip
      title={isTruncated ? name : ""}
      disableHoverListener={!isTruncated}
    >
      <TemplateName ref={ref}>{name}</TemplateName>
    </Tooltip>
  );
}

interface TemplateRowProps {
  template: Template;
  isMobile: boolean;
  isDeleting: boolean;
  onClick: (template: Template) => void;
  onEdit: (e: React.MouseEvent, template: Template) => void;
  onDelete: (template: Template) => void;
}

function TemplateRow({
  template,
  isMobile,
  isDeleting,
  onClick,
  onEdit,
  onDelete,
}: TemplateRowProps) {
  const actionMenu = useActionMenu();

  const description = buildTemplateDescription(template);

  return (
    <TemplateItem onClick={() => onClick(template)}>
      <TemplateItemContent>
        <TruncatedTemplateName name={template.name} />
        {description && <TemplateDescription>{description}</TemplateDescription>}
      </TemplateItemContent>
      {isMobile ? (
        <>
          <IconButton
            size="small"
            aria-label={`Más acciones para plantilla ${template.name}`}
            onClick={(e) => {
              e.stopPropagation();
              actionMenu.open(e);
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <ActionMenu
            anchorEl={actionMenu.anchorEl}
            open={actionMenu.isOpen}
            onClose={actionMenu.close}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem
              disableRipple
              onClick={(e) => {
                onEdit(e, template);
                actionMenu.close();
              }}
            >
              <ListItemIcon>
                <EditOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Editar</ListItemText>
            </MenuItem>
            <MenuItem
              disableRipple
              disabled={isDeleting}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(template);
                actionMenu.close();
              }}
            >
              <ListItemIcon>
                <DeleteOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Eliminar</ListItemText>
            </MenuItem>
          </ActionMenu>
        </>
      ) : (
        <>
          <IconButton
            size="small"
            aria-label={`Editar plantilla ${template.name}`}
            onClick={(e) => onEdit(e, template)}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Eliminar plantilla ${template.name}`}
            disabled={isDeleting}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(template);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      )}
    </TemplateItem>
  );
}

interface TemplatesSectionProps {
  maxItems?: number;
}

export function TemplatesSection({ maxItems }: TemplatesSectionProps) {
  const navigate = useNavigate();
  const { requestId = "" } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const { data: requestData } = useRequestData();
  const { data: templates, isLoading } = useTemplates({
    templateType: TemplateType.PRESCRIPTION,
  });

  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null,
  );

  const { mutate: populateRequest, isPending: isApplying } =
    usePopulateRequestWithTemplate();
  const { mutate: deleteTemplate, isPending: isDeleting } = useDeleteTemplate();

  const hasMoreTemplates =
    maxItems !== undefined &&
    templates !== undefined &&
    templates.length > maxItems;
  const displayedTemplates =
    maxItems !== undefined && templates && !showAllTemplates
      ? templates.slice(0, maxItems)
      : templates;

  const handleTemplateClick = (template: Template) => {
    if (!requestData || isApplying) return;

    populateRequest(
      {
        requestId,
        templateId: template.id,
        patientUuid: requestData.request.patientUuid,
      },
      {
        onSuccess: () => {
          navigate(`/app/solicitudes/${requestId}/receta/crear`, {
            state: { fromTemplate: true, detailAdded: true },
          });
        },
      },
    );
  };

  const handleEditTemplateClick = (e: React.MouseEvent, template: Template) => {
    e.stopPropagation();
    navigate(`/app/solicitudes/${requestId}/plantilla/${template.id}/editar`);
  };

  const handleDeleteTemplate = (template: Template) => {
    if (isDeleting) return;
    setTemplateToDelete(template);
  };

  const handleConfirmDeleteTemplate = () => {
    if (!templateToDelete || isDeleting) return;
    deleteTemplate(
      { templateId: templateToDelete.id },
      {
        onSuccess: () => {
          enqueueSnackbar("Plantilla eliminada", { variant: "success" });
          setTemplateToDelete(null);
        },
        onError: () => {
          enqueueSnackbar("No se pudo eliminar la plantilla", {
            variant: "error",
          });
          setTemplateToDelete(null);
        },
      },
    );
  };

  const handleNewTemplateClick = () => {
    navigate(
      `/app/solicitudes/${requestId}/plantilla/crear/tratamiento/buscar`,
    );
  };

  if (isApplying) {
    return (
      <LoadingOverlay>
        <CircularProgress size={48} />
      </LoadingOverlay>
    );
  }

  return (
    <StyledContainer>
      <HeaderRow>
        <TitleContainer>
          <Title>Plantillas</Title>
        </TitleContainer>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {hasMoreTemplates && (
            <ViewAllButton onClick={() => setShowAllTemplates(true)}>
              Ver todas
            </ViewAllButton>
          )}
          <SimpleHelpText text="Crea plantillas de tus tratamientos más frecuentes para recetar más rápido y usarlas con todos tus pacientes." />
        </Box>
      </HeaderRow>

      <NewTemplateButton onClick={handleNewTemplateClick}>
        Nueva plantilla
      </NewTemplateButton>

      {isLoading ? (
        <LoadingContainer>
          <CircularProgress size={32} />
        </LoadingContainer>
      ) : displayedTemplates && displayedTemplates.length > 0 ? (
        <TemplatesList>
          {displayedTemplates.map((template) => (
            <TemplateRow
              key={template.id}
              template={template}
              isMobile={isMobile}
              isDeleting={isDeleting}
              onClick={handleTemplateClick}
              onEdit={handleEditTemplateClick}
              onDelete={handleDeleteTemplate}
            />
          ))}
        </TemplatesList>
      ) : (
        <EmptyStateText>
          <InfoIcon />
          <Typography variant="body1" sx={{ marginLeft: "8px" }}>
            No tienes plantillas guardadas.
          </Typography>
        </EmptyStateText>
      )}

      <Modal
        title="Eliminar plantilla"
        content={
          templateToDelete
            ? `¿Estás seguro que quieres eliminar la plantilla: ${templateToDelete.name}?`
            : undefined
        }
        isOpen={templateToDelete !== null}
        onClose={isDeleting ? undefined : () => setTemplateToDelete(null)}
        hideCloseIcon
        buttonProps={{
          buttonText: "Eliminar",
          onClick: handleConfirmDeleteTemplate,
          loading: isDeleting,
        }}
        secondaryButtonProps={{
          buttonText: "Cancelar",
          onClick: () => setTemplateToDelete(null),
          disabled: isDeleting,
        }}
      />

      {showAllTemplates && templates && (
        <FullScreenContainer>
          <Header
            title="Plantillas"
            titleColor="black"
            left={
              <NavigateBeforeButton
                onClick={() => setShowAllTemplates(false)}
              />
            }
          />
          <FullScreenTemplatesList>
            <Box sx={{ padding: 2 }}>
              <NewTemplateButton onClick={handleNewTemplateClick}>
                Nueva plantilla
              </NewTemplateButton>
            </Box>
            {templates.map((template) => (
              <TemplateRow
                key={template.id}
                template={template}
                isMobile={isMobile}
                isDeleting={isDeleting}
                onClick={handleTemplateClick}
                onEdit={handleEditTemplateClick}
                onDelete={handleDeleteTemplate}
              />
            ))}
          </FullScreenTemplatesList>
        </FullScreenContainer>
      )}
    </StyledContainer>
  );
}
